import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { BottomSheet, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { WorkBadgeReviewPolicySource } from "@src/appV2/redesign/WorkBadges/components/Policy/types";
import { WorkBadgeReviewPolicyStep } from "@src/appV2/redesign/WorkBadges/hooks/useWorkBadgeReviewPolicy";
import { getWorkBadgeReviewPolicyUrl } from "@src/appV2/redesign/WorkBadges/utils/getWorkBadgeReviewPolicyUrl";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { useGetBadgeVerificationPhotoUrl } from "./api/useGetBadgeVerificationPhotoUrl";
import { useMarkBadgeVerificationResultSeen } from "./api/useMarkBadgeVerificationResultSeen";
import { BadgeVerificationMissedStep } from "./BadgeVerificationMissedStep";
import { BadgeVerificationResultStep } from "./BadgeVerificationResultStep";
import { BadgeVerificationStrikesStep } from "./BadgeVerificationStrikesStep";
import { BadgeVerificationResultType } from "./hooks/useShouldShowBadgeVerificationResult";

enum BadgeVerificationStep {
  SUBMISSION_EXPLANATION = "SUBMISSION_EXPLANATION",
  EXAMPLE_SUBMISSION = "EXAMPLE_SUBMISSION",
}

interface BadgeVerificationResultBottomSheetProps {
  modalState: UseModalState;
  type?: BadgeVerificationResultType;
  strikeCount?: number;
  verificationId?: string;
  shiftId?: string;
  notes?: string;
  qualificationName?: string;
}

export function BadgeVerificationResultBottomSheet(props: BadgeVerificationResultBottomSheetProps) {
  const {
    modalState,
    type,
    strikeCount = 0,
    verificationId,
    shiftId,
    notes,
    qualificationName,
  } = props;

  const history = useHistory();
  const [currentStep, setCurrentStep] = useState<BadgeVerificationStep>(
    BadgeVerificationStep.SUBMISSION_EXPLANATION
  );

  const hasRequiredData = isDefined(type) && isDefined(verificationId) && isDefined(shiftId);
  const isMissed = type === BadgeVerificationResultType.SHOW_MISSED;

  // Only fetch the worker's submitted photo for MISSED — SKIPPED has no photo.
  const {
    data: photoUrlData,
    isLoading: isPhotoLoading,
    isError: isPhotoError,
  } = useGetBadgeVerificationPhotoUrl(isMissed ? verificationId : undefined);
  const { mutateAsync: markSeen, isLoading: isMarkingAsSeen } =
    useMarkBadgeVerificationResultSeen();

  const photoUrl = photoUrlData?.data.attributes.downloadUrl;

  useLogEffect(
    APP_V2_USER_EVENTS.BADGE_VERIFICATION_RESULT_VIEWED,
    { shiftId, verificationId },
    { enabled: modalState.modalIsOpen && hasRequiredData }
  );

  // Reset to the first screen each time the modal re-opens
  useEffect(() => {
    if (modalState.modalIsOpen) {
      setCurrentStep(BadgeVerificationStep.SUBMISSION_EXPLANATION);
    }
  }, [modalState.modalIsOpen]);

  const handleViewBadgePolicy = useCallback(() => {
    if (!isDefined(shiftId)) {
      return;
    }

    // Intentionally leave the modal open so that when the worker navigates back,
    // the post-session modal is restored. We also do not mark the verification as
    // seen here — the hook will reopen the modal on mount because the unseen
    // result is still present.
    history.push(
      getWorkBadgeReviewPolicyUrl({
        source: WorkBadgeReviewPolicySource.BADGE_VERIFICATION,
        shiftId,
        qualificationName: qualificationName ?? "",
        step: WorkBadgeReviewPolicyStep.REVIEW_POLICY,
      })
    );
  }, [history, shiftId, qualificationName]);

  const handleNext = useCallback(() => {
    setCurrentStep(BadgeVerificationStep.EXAMPLE_SUBMISSION);
  }, []);

  const handleGetNewBadge = useCallback(async () => {
    if (isMarkingAsSeen || !isDefined(verificationId) || !isDefined(shiftId)) {
      return;
    }

    try {
      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RESULT_GET_NEW_BADGE_CLICKED, {
        shiftId,
        verificationId,
      });

      await markSeen({ verificationId, shiftId });
      modalState.closeModal();

      history.push(
        getWorkBadgeReviewPolicyUrl({
          source: WorkBadgeReviewPolicySource.BADGE_VERIFICATION,
          shiftId,
          qualificationName: qualificationName ?? "",
          step: isDefined(qualificationName)
            ? WorkBadgeReviewPolicyStep.SELECT_HEADSHOT
            : WorkBadgeReviewPolicyStep.REVIEW_POLICY,
        })
      );
    } catch {
      // Error logging and user toast are handled by the mutation's meta config
    }
  }, [shiftId, verificationId, markSeen, isMarkingAsSeen, modalState, history, qualificationName]);

  const handleAcknowledge = useCallback(async () => {
    if (isMarkingAsSeen || !isDefined(verificationId) || !isDefined(shiftId)) {
      return;
    }

    try {
      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RESULT_DISMISSED, {
        shiftId,
        verificationId,
      });

      await markSeen({ verificationId, shiftId });
      modalState.closeModal();
    } catch {
      // Error logging and user toast are handled by the mutation's meta config
    }
  }, [shiftId, verificationId, markSeen, isMarkingAsSeen, modalState]);

  useEffect(() => {
    const shouldLogPhotoError =
      isMissed &&
      currentStep === BadgeVerificationStep.SUBMISSION_EXPLANATION &&
      !isPhotoLoading &&
      !isDefined(photoUrl) &&
      !isPhotoError;

    if (shouldLogPhotoError) {
      logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_GET_FAILURE, {
        error: "Badge verification photo URL is undefined after loading",
        metadata: { verificationId, shiftId },
      });
    }
  }, [isMissed, currentStep, isPhotoLoading, photoUrl, verificationId, shiftId, isPhotoError]);

  if (!hasRequiredData) {
    return null;
  }

  // Suppress the close button on the first screen so the worker must engage with
  // the strike / result info before being able to dismiss the modal.
  let onClose: (() => void) | undefined;
  if (currentStep !== BadgeVerificationStep.SUBMISSION_EXPLANATION) {
    onClose = modalState.closeModal;
  }

  return (
    <BottomSheet
      canDismiss={false}
      modalState={modalState}
      header={
        <Box
          sx={(theme) => ({
            // Reserve the same height as when the close button is visible so the
            // layout stays consistent across steps (paddingTop + button + paddingBottom).
            minHeight: `calc(${theme.spacing(6)} + ${theme.spacing(9)} + ${theme.spacing(2)})`,
          })}
        >
          <BottomSheetHeader onClose={onClose} />
        </Box>
      }
    >
      {currentStep === BadgeVerificationStep.SUBMISSION_EXPLANATION && isMissed && (
        <BadgeVerificationMissedStep
          strikeCount={strikeCount}
          notes={notes}
          isPhotoLoading={isPhotoLoading}
          photoUrl={photoUrl}
          onViewBadgePolicy={handleViewBadgePolicy}
          onNext={handleNext}
        />
      )}

      {currentStep === BadgeVerificationStep.SUBMISSION_EXPLANATION && !isMissed && (
        <BadgeVerificationStrikesStep
          type={type}
          strikeCount={strikeCount}
          onViewBadgePolicy={handleViewBadgePolicy}
          onNext={handleNext}
        />
      )}

      {currentStep === BadgeVerificationStep.EXAMPLE_SUBMISSION && (
        <BadgeVerificationResultStep
          isMarkingAsSeen={isMarkingAsSeen}
          onGetNewBadge={handleGetNewBadge}
          onAcknowledge={handleAcknowledge}
        />
      )}
    </BottomSheet>
  );
}
